import {
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  useLayoutEffect,
} from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useSuspension } from '../../context/SuspensionContext';
import { useConfettiText } from '../../hooks/useConfettiText';
import Picker from 'emoji-picker-react';
import { ClipLoader } from 'react-spinners';
import axiosConn from '../../axiosConn';
import useClickOutside from '../../clickOutside';
import { useMediaQuery } from 'react-responsive';
import wordList from 'word-list-json';
import { handleSuspension } from '../../utils/suspensionUtil';
// NEW unified uploader util (Better-than-FAANG+)
import { createMediaUploader } from '../../utils/mediaUpload';
import './createComment.css';

const wordSet = new Set(wordList);

export default function CreateComment({
  memeId,
  postId,
  setComments,
  setCommentCount,
  parent,
  socket2,
  size = 'auto', // 'auto' | 'comfortable' | 'compact' | 'xs' | 'xxs'
}) {
  const { player } = useContext(AuthContext);
  const { isSuspended, setIsSuspended } = useSuspension();

  const [picker, setPicker] = useState(false);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null); // raw File (image)
  const [commentImage, setCommentImage] = useState(''); // DataURL preview
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [commentTimestamps, setCommentTimestamps] = useState([]);
  const [cursorPosition, setCursorPosition] = useState();

  const COMMENT_LIMIT = 5;
  const TIME_FRAME = 60000;
  const SUSPENSION_TIME = 3600000;

  const textRef = useRef(null);
  const imgInput = useRef(null);
  const pickerMenuRef = useRef(null);
  const dndRef = useRef(null);
  const containerRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [isExtraNarrow, setIsExtraNarrow] = useState(false);
  const [isUltraNarrow, setIsUltraNarrow] = useState(false); // xxs trigger
  const isMemeContext = parent === 'Meme' || parent === 'MemeRotator';
  const lastTypingEmitRef = useRef(0);

  const mobileView = useMediaQuery({ query: '(max-width: 805px)' });

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    textRef,
    mobileView,
    '+5 JGK',
    600,
    'comment',
  );

  const isCommentEmpty = useMemo(
    () => text.trim() === '' && !commentImage,
    [text, commentImage],
  );

  useClickOutside(pickerMenuRef, () => setPicker(false));

  // --- Responsive size auto detection ---
  useLayoutEffect(() => {
    if (size !== 'auto' || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setIsUltraNarrow(w < 330);
      setIsExtraNarrow(w < 360);
      setIsNarrow(w < 420);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [size]);

  useEffect(() => {
    if (cursorPosition != null && textRef.current) {
      textRef.current.selectionEnd = cursorPosition;
      textRef.current.selectionStart = cursorPosition;
    }
  }, [cursorPosition]);

  const togglePicker = () => setPicker((p) => !p);

  const handleEmoji = (_, { emoji }) => {
    const ref = textRef.current;
    if (!ref) return;
    ref.focus();
    const start = ref.value.substring(0, ref.selectionStart);
    const end = ref.value.substring(ref.selectionStart);
    const newText = start + emoji + end;
    setText(newText);
    setCursorPosition(start.length + emoji.length);
  };

  // === Unified Media Uploader (images only for comments) ===
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({
      maxImageBytes: 10 * 1024 * 1024, // Preserve legacy 10MB image limit
      maxVideoBytes: 0, // Not used here
      // Accept only images (validation will reject others)
      // Use default Cloudinary config via env vars or fallback inside util
    });
  }
  const uploader = uploaderRef.current;

  const stageFileForPreview = (f) => {
    setFile(f);
    // Use DataURL for existing preview logic (backward compatible)
    const reader = new FileReader();
    reader.onload = (ev) => setCommentImage(ev.target.result);
    reader.readAsDataURL(f);
  };

  const validateAndStageFile = (f) => {
    try {
      uploader.validate(f); // throws if invalid (type/size)
      setError('');
      stageFileForPreview(f);
    } catch (err) {
      setError(err.message || `${f.name} is not supported.`);
    }
  };

  const handleImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    validateAndStageFile(f);
  };

  const emitTyping = () => {
    if (!socket2 || !postId) return;
    const now = Date.now();
    if (now - lastTypingEmitRef.current < 800) return;
    lastTypingEmitRef.current = now;
    socket2.emit('post:comment:typing', {
      postId,
      playerId: player?.data?.player?.playerId,
    });
  };

  // Drag & Drop handlers (unchanged except using new validator)
  const onDragOver = (e) => {
    e.preventDefault();
    dndRef.current?.classList.add('dndHover');
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dndRef.current?.classList.remove('dndHover');
  };
  const onDrop = (e) => {
    e.preventDefault();
    dndRef.current?.classList.remove('dndHover');
    if (e.dataTransfer.files?.[0])
      validateAndStageFile(e.dataTransfer.files[0]);
  };

  const isGibberish = (txt) => {
    const repeatedChars = /(.)\1{3,}/;
    if (repeatedChars.test(txt)) return true;
    const words = txt.split(/\s+/);
    const meaningful = words.filter(
      (w) => w.length > 2 && wordSet.has(w.toLowerCase()),
    );
    return meaningful.length < words.length / 2;
  };

  const resetMedia = () => {
    setFile(null);
    setCommentImage('');
    setUploadPct(0);
    if (imgInput.current) imgInput.current.value = '';
  };

  // === Upload via unified util (images only) ===
  const uploadImage = async () => {
    if (!file) return '';
    const { transformedUrl } = await uploader.upload(file, {
      publicIdPrefix: 'commentImages',
      onProgress: (pct) => setUploadPct(pct),
    });
    return transformedUrl;
  };

  const postCommentToBackend = async (imageUrl) => {
    if (isMemeContext) {
      const comments = await axiosConn.put(
        '/memes/comment',
        { comment: text, memeId, image: imageUrl || '' },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      setComments(comments.data);
      setCommentCount((p) => ++p);
      showConfettiText();
      socket2?.emit?.('updateMemeComments', {
        memeId,
        comments: comments.data,
      });
    } else {
      const comments = await axiosConn.put(
        '/posts/comment',
        { comment: text, postId, image: imageUrl || '' },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      setComments(comments.data);
      setCommentCount((p) => ++p);
      showConfettiText();
      socket2?.emit?.('updatePostComments', {
        postId,
        comments: comments.data,
      });
    }
  };

  const handleComment = async (e) => {
    if (!(e.type === 'click' || e.key === 'Enter')) return;
    if (loading || isSuspended) return;

    const trimmed = text.trim();
    if (trimmed === '' && !commentImage) {
      setError('Add text or an image before commenting.');
      return;
    }
    if (trimmed.length > 2048) {
      setError('Comment too long (2048 max).');
      return;
    }

    const suspendedNow = await handleSuspension(
      commentTimestamps,
      setCommentTimestamps,
      setIsSuspended,
      COMMENT_LIMIT,
      TIME_FRAME,
      SUSPENSION_TIME,
      player.data.player._id,
    );
    if (suspendedNow) return;

    setError('');
    setLoading(true);
    if (picker) setPicker(false);

    try {
      let uploadedUrl = '';
      if (file) {
        // Defensive re-validation (in case limits changed dynamically)
        try {
          uploader.validate(file);
        } catch (err) {
          setError(err.message || 'Invalid image.');
          setLoading(false);
          return;
        }
        uploadedUrl = await uploadImage();
      }
      await postCommentToBackend(uploadedUrl);
      setText('');
      resetMedia();
    } catch (err) {
      console.error(err);
      if (err.message === 'Upload canceled') {
        setError('Upload canceled.');
      } else {
        setError('Failed to submit comment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') handleComment(e);
  };

  const progressVisible = uploadPct > 0 && uploadPct < 100;

  const sizeClass =
    size === 'xxs'
      ? 'ccXXS'
      : size === 'xs'
      ? 'ccXS'
      : size === 'compact'
      ? 'ccCompact'
      : size === 'comfortable'
      ? 'ccComfortable'
      : isUltraNarrow
      ? 'ccXXS'
      : isExtraNarrow
      ? 'ccXS'
      : isNarrow
      ? 'ccCompact'
      : 'ccComfortable';

  const contextClass = parent === 'MemeRotator' ? 'ccRotator' : '';

  return (
    <div
      className={`create_comment_wrap ccComposer ${sizeClass} ${contextClass}`}
      ref={(el) => {
        dndRef.current = el;
        containerRef.current = el;
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <ConfettiTextComponent />

      {isSuspended && (
        <div className="ccSuspended">
          You are temporarily suspended due to excessive commenting. Try later.
        </div>
      )}

      <div className="create_comment">
        <img
          src={
            player?.data?.player?.profilePhoto
              ? player.data.player.profilePhoto
              : 'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1658712082/Images/noAvatar_tsgpm9.png'
          }
          alt=""
          className="createCommentAvatar"
          draggable="false"
        />

        <div
          className="comment_input_wrap commentShell"
          data-filled={!isCommentEmpty}
        >
          {picker && (
            <div className="comment_emoji_picker" ref={pickerMenuRef}>
              <Picker
                onEmojiClick={handleEmoji}
                theme="light"
                autoFocus
                lazyLoadEmojis
              />
            </div>
          )}

          <input
            type="file"
            hidden
            ref={imgInput}
            // Keep accept list consistent with legacy behavior
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImage}
          />

          {error && (
            <div className="postError comment_error ccError">
              <div className="postError_error">{error}</div>
              <button
                className="blue_btn"
                type="button"
                onClick={() => setError('')}
              >
                Dismiss
              </button>
            </div>
          )}

          <input
            className="commentInputText"
            type="text"
            ref={textRef}
            value={text}
            placeholder="Write a comment..."
            onChange={(e) => {
              setText(e.target.value);
              emitTyping();
            }}
            onKeyUp={handleKeyUp}
            enterKeyHint="send"
            aria-label="Comment text input"
            maxLength={2048}
          />

          <div className="comment_circle statusCircle">
            <ClipLoader size={18} color="#4f8dff" loading={loading} />
          </div>

          <div
            className="comment_circle_icon ccTool"
            role="button"
            tabIndex={0}
            aria-label="Add emoji"
            onClick={() => !loading && togglePicker()}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && togglePicker()
            }
            title="Emoji"
          >
            <span className="ccEmojiSymbol" aria-hidden="true">
              😊
            </span>
          </div>

          <div
            className="comment_circle_icon ccTool"
            role="button"
            tabIndex={0}
            aria-label="Add image"
            onClick={() => !loading && imgInput.current?.click()}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && imgInput.current?.click()
            }
            title="Attach image"
          >
            <span className="ccPlusSymbol" aria-hidden="true">
              +
            </span>
          </div>

          <div
            className={
              'comment_circle_icon ccSendBtn ' +
              (isCommentEmpty || loading ? 'ccSendDisabled' : 'ccSendReady')
            }
            role="button"
            tabIndex={0}
            aria-label="Submit comment"
            onClick={handleComment}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && handleComment(e)
            }
            title={
              isCommentEmpty
                ? 'Add text or image'
                : loading
                ? 'Posting...'
                : 'Send'
            }
          >
            <svg
              className="ccSendIcon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M3.15 10.75c-.97-.37-.97-1.76 0-2.13L19.8 3.06c.9-.34 1.74.5 1.39 1.39l-5.56 14.6c-.37.97-1.76.97-2.13 0l-1.77-4.65a1 1 0 0 0-.6-.6l-7.98-3.05Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </div>

      {commentImage && (
        <div className="comment_img_preview">
          <img
            src={commentImage}
            alt="preview"
            className="commentPreviewImg"
            draggable="false"
          />
          <div
            className="small_white_circle ccRemoveImg"
            role="button"
            tabIndex={0}
            aria-label="Remove image"
            onClick={resetMedia}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && resetMedia()
            }
            title="Remove image"
          >
            <span className="ccX">&times;</span>
          </div>
          {progressVisible && (
            <div className="uploadProgressMini ccMiniProg">
              <div
                className="uploadProgressMiniBar ccMiniProgBar"
                style={{ width: `${uploadPct}%` }}
              />
              <span className="uploadProgressMiniLabel">{uploadPct}%</span>
            </div>
          )}
        </div>
      )}

      {!commentImage && (
        <div className="dndHint ccHint" aria-hidden="true">
          Drag & drop an image or use the +
        </div>
      )}
    </div>
  );
}
